import { useCallback, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { resetAccountPassword, type AccountPasswordReset } from '@/api/console';
import {
    createOrgAssignment,
    OrgAdminApiError,
    revokeOrgAssignment,
    type ScopedAccountRow,
} from '@/api/orgAdmin';
import type { AssignLevelFormModel } from '@/pages/console/hierarchy/AssignLevelForm';
import type { ScopedAccountsVM } from './useScopedAccounts';

/** Typed against the shared model so this form and the tree's cannot drift apart. */
const EMPTY_ADD_FORM: AssignLevelFormModel = { user_sub: '', user_email: '', level_id: '' };

/**
 * EAA-1839: the single-row writes of the scoped accounts browser — everything the
 * "⋯" menu and the "+ Tài khoản" button do.
 *
 * WHAT IS DELIBERATELY ABSENT, and why. Frame 34's bulk bar offers four actions;
 * only ONE of them has a bulk endpoint. `authoring_views.py` ships exactly
 * `assignments/bulk-preview` and `assignments/bulk-apply` (Level assign). There is
 * no bulk change-đơn-vị, no bulk password reset and no bulk deactivate anywhere in
 * portal-api — and this ticket's own scope note puts "Đổi đơn vị hàng loạt" in a
 * separate ticket. Those three bulk buttons are therefore rendered DISABLED with
 * the real reason (see `ScopedAccountsBulkBar`), never looped over a single-row
 * endpoint behind a bulk-looking label.
 *
 * Two single-row actions ARE real and are exposed per row instead:
 *   - "Thu hồi quyền" → `DELETE /organizations/{id}/assignments/{assignmentId}`,
 *     a soft revoke (the row stays, `is_active` flips, the revoke is pushed).
 *   - "Đặt lại mật khẩu" → `POST /accounts/reset-password`, which is
 *     `IsSuperAdmin` server-side, so it is gated on `console.accounts.reset` and
 *     simply absent for an org-admin.
 *
 * There is still no way to disable a Keycloak account (no PATCH for `enabled`) and
 * no way to move an account between đơn vị, in bulk OR singly — so neither appears
 * in the row menu either.
 */
export function useScopedAccountWrites(list: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const { orgId, invalidateList, activeLevels } = list;

    const fail = useCallback(
        (fallback: string) => (error: unknown) =>
            toast.error(error instanceof OrgAdminApiError ? error.message : fallback),
        [toast]
    );

    /* ------------------------------------------------------- revoke a grant */

    const [revokeTarget, setRevokeTarget] = useState<ScopedAccountRow | null>(null);

    const revokeMutation = useMutation({
        mutationFn: (row: ScopedAccountRow) =>
            // EAA-1839: the URL org is the ROW's own organization, not the scope
            // org — with "Gồm đơn vị con" on, a row can belong to a descendant
            // đơn vị, and `OrganizationAssignmentDetailView` resolves the
            // assignment inside the org named in the path.
            revokeOrgAssignment(config.apiBase, accessToken as string, row.organization_id, row.id),
        onSuccess: (_data, row) => {
            setRevokeTarget(null);
            invalidateList();
            toast.success(
                t('console.hierarchy.scopedAccounts.writes.revokeSuccess', {
                    identity: row.user_email || row.user_sub,
                })
            );
        },
        onError: fail(t('console.hierarchy.scopedAccounts.writes.revokeErrorFallback')),
    });

    /* ---------------------------------------------------- reset a password */

    const [resetTarget, setResetTarget] = useState<ScopedAccountRow | null>(null);
    const [resetResult, setResetResult] = useState<AccountPasswordReset | null>(null);

    const resetMutation = useMutation({
        mutationFn: (row: ScopedAccountRow) =>
            resetAccountPassword(config.apiBase, accessToken as string, { sub: row.user_sub }),
        onSuccess: (data) => {
            setResetTarget(null);
            setResetResult(data);
            toast.success(
                t('console.hierarchy.scopedAccounts.writes.resetSuccess', {
                    username: data.username,
                })
            );
        },
        onError: fail(t('console.hierarchy.scopedAccounts.writes.resetErrorFallback')),
    });

    /* -------------------------------------------------- "+ Tài khoản" grant */

    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState<AssignLevelFormModel>(EMPTY_ADD_FORM);
    const [addError, setAddError] = useState<string | null>(null);

    const openAdd = useCallback(() => {
        setAddForm(EMPTY_ADD_FORM);
        setAddError(null);
        setAddOpen(true);
    }, []);
    const closeAdd = useCallback(() => setAddOpen(false), []);

    const addMutation = useMutation({
        mutationFn: () =>
            createOrgAssignment(config.apiBase, accessToken as string, orgId as string, {
                user_sub: addForm.user_sub.trim(),
                ...(addForm.user_email.trim() ? { user_email: addForm.user_email.trim() } : {}),
                level_id: addForm.level_id,
            }),
        onSuccess: (assignment) => {
            setAddOpen(false);
            setAddForm(EMPTY_ADD_FORM);
            invalidateList();
            toast.success(
                t('console.hierarchy.scopedAccounts.writes.addSuccess', {
                    identity: assignment.user_email || assignment.user_sub,
                })
            );
        },
        onError: (error: unknown) => {
            const message =
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('console.hierarchy.scopedAccounts.writes.addErrorFallback');
            setAddError(message);
            toast.error(message);
        },
    });

    // EAA-1839: takes the FormEvent because the shared `AssignLevelForm` is a real
    // <form> submitted by the Modal footer's `form="…"` button — the same wiring the
    // tree's "Cấp quyền" modal uses, so both screens validate on submit rather than
    // one on submit and one on click.
    const submitAdd = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            if (!addForm.user_sub.trim()) {
                setAddError(
                    t('console.hierarchy.scopedAccounts.writes.validationUserSubRequired')
                );
                return;
            }
            if (!addForm.level_id) {
                setAddError(t('console.hierarchy.scopedAccounts.writes.validationLevelRequired'));
                return;
            }
            setAddError(null);
            addMutation.mutate();
        },
        [addForm, addMutation, t]
    );

    return {
        revokeTarget,
        setRevokeTarget,
        runRevoke: revokeMutation.mutate,
        revokeBusy: revokeMutation.isPending,

        resetTarget,
        setResetTarget,
        runReset: resetMutation.mutate,
        resetBusy: resetMutation.isPending,
        resetResult,
        dismissResetResult: () => setResetResult(null),

        addOpen,
        openAdd,
        closeAdd,
        addForm,
        setAddForm,
        addError,
        submitAdd,
        addBusy: addMutation.isPending,
        addLevels: activeLevels,
    };
}

export type ScopedAccountWritesVM = ReturnType<typeof useScopedAccountWrites>;
